import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAuthMe,
  fetchTenantBilling,
  setBilling as setAuthBilling,
} from "../../../store/authSlice";
import authAPI from "../../../services/authApi";
import clinicAPI from "../../../services/clinicApi";
import { isPhoneComplete, normalizePhoneRu } from "../../../utils/phoneMask";
import { trackPaymentSuccess } from "../../../analytics/metrikaGoals";
import { useToast } from "../../ui";

export function useOwnerProfileForm() {
  const dispatch = useDispatch();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useSelector((state) => state.auth);
  const [form, setForm] = useState({ full_name: "", phone: "" });
  const [pwd, setPwd] = useState({ current: "", next: "" });
  const [billing, setBilling] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);

  const loadBilling = useCallback(async () => {
    try {
      const next = await dispatch(fetchTenantBilling()).unwrap();
      setBilling(next || null);
    } catch {
      setBilling(null);
      dispatch(setAuthBilling(null));
    }
  }, [dispatch]);

  useEffect(() => {
    setForm({
      full_name: profile?.full_name || "",
      phone: profile?.phone || "",
    });
  }, [profile]);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  useEffect(() => {
    if (searchParams.get("payment") === "success") {
      trackPaymentSuccess({ source: "club_profile" });
      toast.success("Оплата принята. Обновляем подписку…");
      searchParams.delete("payment");
      setSearchParams(searchParams, { replace: true });
      // getBilling на бэке синхронизирует оплату с ЮKassa, если webhook не успел
      loadBilling();
      dispatch(fetchAuthMe());
    }
  }, [searchParams, setSearchParams, loadBilling, dispatch, toast]);

  const saveProfile = async () => {
    const phone = form.phone?.trim() ? normalizePhoneRu(form.phone) : "";
    if (form.phone?.trim() && !isPhoneComplete(phone)) {
      toast.error("Укажите корректный номер телефона (10 цифр)");
      return;
    }
    setSaving(true);
    try {
      await authAPI.updateMyAccount({
        full_name: form.full_name,
        phone: phone || null,
      });
      await dispatch(fetchAuthMe());
      toast.success("Сохранено");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    if (!pwd.current || !pwd.next) {
      toast.error("Заполните оба поля пароля");
      return;
    }
    setPwdSaving(true);
    try {
      await authAPI.changePassword(pwd.current, pwd.next);
      setPwd({ current: "", next: "" });
      toast.success("Пароль изменён");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setPwdSaving(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      await clinicAPI.uploadStaffMemberPhoto(file);
      await dispatch(fetchAuthMe());
      toast.success("Фото обновлено");
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка загрузки");
    } finally {
      setPhotoUploading(false);
      e.target.value = "";
    }
  };

  return {
    profile,
    form,
    setForm,
    pwd,
    setPwd,
    billing,
    saving,
    pwdSaving,
    photoUploading,
    saveProfile,
    savePassword,
    uploadPhoto,
    loadBilling,
  };
}
